// ============================================================================
// qm-quote-no.js — QM-issued Quote Number allocation helper (frontend seam)
// ----------------------------------------------------------------------------
// Allocates (or returns existing) the QM-issued Quote Number for a project by
// calling the backend POST /api/projects/:id/allocate-qm-quote-no route added
// in migration 070 (2026-05-20).
//
// Caller contract:
//   * Idempotent: safe to call on every send-quote-email confirmation. Only
//     the first call allocates a new number; subsequent calls return the
//     existing number.
//   * Returns { qmQuoteNo, allocated } on success.
//   * Returns null on failure (network, 4xx, 5xx). Caller logs + continues —
//     allocation must NOT block email send.
//   * Returns null, quietly, for a quote imported with another builder's
//     number (the server mints nothing for it, docs/job/GOAL.md A7).
//   * A 409 for the 99-a-month cap is remembered per project and read back
//     with getQmQuoteNoAllocationProblem(projectId).
//
// The new field flows back through PUT /api/projects/:id naturally because
// the route's UPDATE clause uses COALESCE($field, qm_quote_no), preserving
// the server-stamped value on subsequent saves.
// ============================================================================

import { apiFetch } from './data-service-api.js';
import { activeQuoteNumberPrefix } from './business-quote-prefix';

export interface AllocateQmQuoteNoResult {
    qmQuoteNo: string;
    allocated: boolean;
}

export type ValidateQmQuoteNoResult =
    | { ok: true; value: string }
    | { ok: false; error: string };

export interface SetQmQuoteNoOverrideResult {
    qmQuoteNo: string | null;
    /** The project's new version (its save token) after the write. */
    updatedAt?: string | null;
    /** The version the write replaced; the tab adopts updatedAt only when this is its own token. */
    previousUpdatedAt?: string | null;
}

/** Why the last allocation for a project gave no Quote #, in words the editor can show. */
export interface QmQuoteNoAllocationProblem {
    code: string;
    message: string;
}

/** The server's code for "this month's 99 Quote numbers are all used". */
export const QM_MONTHLY_OVERFLOW = 'QM_MONTHLY_OVERFLOW';

const MONTHLY_OVERFLOW_FALLBACK_MESSAGE =
    'All 99 Quote numbers for this month are used. A new number can be issued next month.';

// Job workstream (docs/job/GOAL.md A6, A7): the last allocation problem per
// project, so the editor can say why a quote has no Quote # instead of
// showing a blank. Only the monthly cap is recorded: a network blip or a
// retryable conflict says nothing lasting about the project. Cleared when a
// later answer for the same project is a success, or when the operator sets
// a number by hand (setQmQuoteNoOverride).
const _allocationProblems = new Map<string, QmQuoteNoAllocationProblem>();

/**
 * The last recorded allocation problem for a project, or null. Today the
 * only recorded code is QM_MONTHLY_OVERFLOW.
 */
export function getQmQuoteNoAllocationProblem(projectId?: string | null): QmQuoteNoAllocationProblem | null {
    if (!projectId || typeof projectId !== 'string') return null;
    const problem = _allocationProblems.get(projectId);
    return problem ? { ...problem } : null;
}

/**
 * The allocate route answers 409 with "Monthly quote allocation cap reached
 * (99/month) ..." when the month's counter is spent (backend/src/routes/
 * projects.js, allocate-qm-quote-no). apiFetch puts that sentence in
 * err.message and the parsed body in err.body. A 409 without that wording is
 * the retryable number conflict, which is not recorded.
 */
function readMonthlyOverflow(err: any): QmQuoteNoAllocationProblem | null {
    if (!err || Number(err.status) !== 409) return null;
    const body = err.body && typeof err.body === 'object' ? err.body : {};
    const bodyText = typeof body.error === 'string' ? body.error.trim() : '';
    const messageText = typeof err.message === 'string' ? err.message.trim() : '';
    const isOverflow = err.code === QM_MONTHLY_OVERFLOW
        || body.code === QM_MONTHLY_OVERFLOW
        || bodyText.includes('99/month')
        || messageText.includes('99/month');
    if (!isOverflow) return null;
    return {
        code: QM_MONTHLY_OVERFLOW,
        message: bodyText || messageText || MONTHLY_OVERFLOW_FALLBACK_MESSAGE,
    };
}

/**
 * A quote imported from another builder keeps that builder's number, so the
 * server answers 200 { qmQuoteNo: null, allocated: false, sourceQuoteRef }
 * and mints nothing (GOAL A7). That is an expected answer, not a failure.
 */
function isExternalQuoteAnswer(data: any): boolean {
    return !!data
        && typeof data === 'object'
        && data.qmQuoteNo == null
        && data.allocated !== true
        && typeof data.sourceQuoteRef === 'string'
        && data.sourceQuoteRef.trim().length > 0;
}

// v5.37.21 audit fix (code-reviewer FINDING B3): module-scoped in-flight
// dedup Map<projectId, Promise>. Pre-fix App.jsx had a Set + FullQuoteEditorModal
// had its OWN Set + AllQuotesPage had no dedup at all — three caller paths
// could fire three concurrent POSTs for the same projectId. Backend was
// idempotent so data integrity held, but each fire cost a roundtrip + a
// duplicate `[QM Quote #] Allocated ...` log line. Now ALL callers share a
// single in-flight Map. If a request is already in flight for the same
// projectId, return its Promise instead of starting a new one. Cleared in
// finally so a failure on attempt N+1 retries cleanly.
const _allocateInFlight = new Map();

/**
 * Allocate (or retrieve existing) the QM Quote Number for a project.
 *
 * @param {string} projectId — UUID of the project on the backend.
 * @returns {Promise<{ qmQuoteNo: string, allocated: boolean } | null>}
 */
export async function allocateQmQuoteNo(projectId?: string | null): Promise<AllocateQmQuoteNoResult | null> {
    if (!projectId || typeof projectId !== 'string') {
        console.warn('[qm-quote-no] allocate skipped: invalid projectId', projectId);
        return null;
    }
    const existing = _allocateInFlight.get(projectId);
    if (existing) {
        // Same project already in flight — share the promise. The caller
        // sees the SAME resolution as whoever started it.
        return existing;
    }
    const promise = (async () => {
        try {
            const data = await apiFetch(`/projects/${projectId}/allocate-qm-quote-no`, {
                method: 'POST',
            });
            if (isExternalQuoteAnswer(data)) {
                // Imported quote with another builder's number: quiet, no warning.
                _allocationProblems.delete(projectId);
                return null;
            }
            if (!data || typeof data.qmQuoteNo !== 'string') {
                console.warn('[qm-quote-no] allocate returned unexpected shape:', data);
                return null;
            }
            _allocationProblems.delete(projectId);
            return { qmQuoteNo: data.qmQuoteNo, allocated: !!data.allocated };
        } catch (err: any) {
            // Allocation failure must never block email send. Surface as a console
            // warning; the project simply ships without a qmQuoteNo and the next
            // send-attempt will retry. Same idempotence safety net applies.
            // The monthly cap is also recorded so the editor can say it.
            const overflow = readMonthlyOverflow(err);
            if (overflow) _allocationProblems.set(projectId, overflow);
            console.warn('[qm-quote-no] allocate failed:', err?.message || err);
            return null;
        } finally {
            _allocateInFlight.delete(projectId);
        }
    })();
    _allocateInFlight.set(projectId, promise);
    return promise;
}

/**
 * Format helper for display. Currently a passthrough — the backend already
 * returns the canonical `Q-YYYY-NNNN` shape — but centralises the prefix
 * label ("Quote #") so future copy changes touch one site.
 *
 * v5.37.16: label changed from "Quote No." to "Quote #" — the QM-issued
 * identifier IS the canonical "Quote #" going forward. The QR Number from
 * the requester (field `quoteNumber` in JSON, `quote_number` column in PG)
 * is now labelled "Quote Request #" everywhere it's user-visible.
 *
 * @param {string|null|undefined} qmQuoteNo
 * @returns {string} — formatted label, or '' when no number is present.
 */
export function formatQmQuoteNoLabel(qmQuoteNo?: string | null): string {
    if (!qmQuoteNo || typeof qmQuoteNo !== 'string') return '';
    return `Quote # ${formatQmQuoteNoDisplay(qmQuoteNo)}`;
}

/**
 * Display-format helper for the raw canonical YYMMXX value. Prepends the
 * business's quote prefix (e.g. "RBB" for RJ Bird) to produce the user-facing
 * string "RBB260522" while the database, validation, and uniqueness
 * constraints continue to operate on the raw 6-digit YYMMXX.
 *
 * Skip cases (returns input as-is):
 *   - empty / non-string → '' (matches formatQmQuoteNoLabel's contract)
 *   - already starts with a letter → operator-edited or legacy override
 *     ("Q-2026-0001"), already prefixed ("RBB260522"), or any human alpha
 *     identifier — leave it alone so the helper is idempotent and won't
 *     double-prefix on re-display.
 *   - the business's prefix is not known yet → pass through unchanged.
 *
 * @param {string|null|undefined} rawQuoteNo
 * @returns {string} — prefixed display string, or '' when no number.
 */
export function formatQmQuoteNoDisplay(rawQuoteNo?: string | null): string {
    if (!rawQuoteNo || typeof rawQuoteNo !== 'string') return '';
    const trimmed = rawQuoteNo.trim();
    if (!trimmed) return '';
    if (/^[A-Za-z]/.test(trimmed)) return trimmed; // already prefixed / legacy
    // The business's own prefix (services/business-quote-prefix.ts): its
    // stored quote_number_prefix (migration 091), or its business name's
    // initials when it set none (decision D5 = O1). RJ Bird stores RBB, so its
    // numbers are unchanged. Sync read because this is a render-path
    // formatter. No business borrows RJ Bird's "RBB": until the prefix is
    // known (the profile and the sign-in answer both missing), the number
    // prints bare.
    const prefix = activeQuoteNumberPrefix();
    return prefix ? `${prefix}${trimmed}` : trimmed;
}

/**
 * Client-side validation mirror of the backend's validateQmQuoteNoOverride.
 * Used by the inline-edit affordance to give the operator immediate feedback
 * BEFORE round-tripping to the server. The server still re-validates — never
 * trust the client.
 *
 * @param {string} value — proposed override (raw, untrimmed).
 * @returns {{ ok: true, value: string } | { ok: false, error: string }}
 */
export function validateQmQuoteNoOverrideClient(value: unknown): ValidateQmQuoteNoResult {
    if (typeof value !== 'string') {
        return { ok: false, error: 'Value must be text' };
    }
    const trimmed = value.trim();
    if (trimmed.length === 0) {
        return { ok: false, error: 'Quote # cannot be empty' };
    }
    // v5.38.1 (Peter 2026-05-21): canonical format is YYMMXX (6 digits).
    // YY = year-of-century, MM = month (01-12), XX = counter 01-99. The
    // common operator-edit case is typing in this format; check it first
    // with helpful month-range guidance.
    if (/^\d{6}$/.test(trimmed)) {
        const month = parseInt(trimmed.slice(2, 4), 10);
        if (month < 1 || month > 12) {
            return { ok: false, error: 'Month (digits 3-4) must be 01..12 — e.g. 260501 for May 2026' };
        }
        const counter = parseInt(trimmed.slice(4, 6), 10);
        if (counter < 1) {
            return { ok: false, error: 'Counter (digits 5-6) must be 01..99' };
        }
        return { ok: true, value: trimmed };
    }
    // Legacy/admin fallback — accept the pre-v5.38.1 Q-YYYY-NNNN shape and
    // any other alphanumeric/dash/underscore identifier so existing quotes
    // can still be edited.
    if (trimmed.length > 32) {
        return { ok: false, error: 'Max 32 characters' };
    }
    if (!/^[A-Za-z0-9_-]+$/.test(trimmed)) {
        return { ok: false, error: 'Use 6 digits (YYMMXX, e.g. 260501) or a legacy identifier (letters, digits, dash, underscore)' };
    }
    return { ok: true, value: trimmed };
}

/**
 * Build a contextual placeholder for the Quote # input that reflects the
 * current month — so the operator types into "260501" rather than guessing
 * a stale example. Exported so the modal can use it without hard-coding.
 */
export function getCurrentMonthQmQuoteNoPlaceholder(date: Date = new Date()): string {
    const yy = String(date.getUTCFullYear()).slice(-2);
    const mm = String(date.getUTCMonth() + 1).padStart(2, '0');
    return `${yy}${mm}01`;
}

/**
 * PATCH the qmQuoteNo column for a single project. Used by the Stage 5
 * inline-edit affordance. Mirrors the backend's focused PATCH endpoint
 * (avoids the full-PUT clobber surface).
 *
 * @param {string} projectId — UUID of the project.
 * @param {string|null} qmQuoteNo — new value, or null to clear the override.
 * @returns {Promise<{ qmQuoteNo: string | null }>} — fresh value on success.
 * @throws Error with a `.statusCode` + `.fieldError` on validation failure (400)
 *         OR a `.statusCode === 409` on uniqueness conflict.
 */
export async function setQmQuoteNoOverride(projectId: string, qmQuoteNo: string | null): Promise<SetQmQuoteNoOverrideResult> {
    if (!projectId || typeof projectId !== 'string') {
        const err = new Error('Invalid projectId') as Error & { statusCode?: number };
        err.statusCode = 400;
        throw err;
    }
    // apiFetch attaches `err.status` (HTTP code) and `err.body` (parsed JSON)
    // on non-2xx responses. We rethrow as-is so the caller can read those
    // fields directly — the inline-edit UI surfaces err.body.error verbatim
    // when present, falls back to err.message otherwise.
    const data = await apiFetch(`/projects/${projectId}/qm-quote-no`, {
        method: 'PATCH',
        body: JSON.stringify({ qmQuoteNo }),
    });
    const saved = data?.qmQuoteNo ?? null;
    // A number typed by hand answers a recorded monthly-cap problem the same
    // way a later allocation does, so the editor stops reporting the cap.
    // Clearing the number keeps the record: the quote has no number again.
    if (typeof saved === 'string' && saved.trim()) _allocationProblems.delete(projectId);
    // The write advances the project's save token. Hand the version receipt
    // on so the editor can offer it to adoptServerProjectVersion.
    const result: SetQmQuoteNoOverrideResult = { qmQuoteNo: saved };
    if (data && typeof data === 'object') {
        if ('updatedAt' in data) result.updatedAt = data.updatedAt ?? null;
        if ('previousUpdatedAt' in data) result.previousUpdatedAt = data.previousUpdatedAt ?? null;
    }
    return result;
}
